<template>
  <v-form ref="form" @submit.prevent>
    <div class="mb-1">
      <small class="grey--text">Název připravené notifikace *</small>
    </div>
    <InputElement
      v-model="form.name"
      label="Název (interní popis)"
      :rules="[rules.required, rules.max200]"
      counter="200"
    />

    <div class="mb-1"><small class="grey--text">Nadpis *</small></div>
    <div class="mb-1">
      <small class="text--disabled text-caption"
        >Doporučeno max. 50 znaků, jinak se na mobilu může zkracovat</small
      >
    </div>
    <InputElement
      v-model="form.title"
      label="Nadpis notifikace"
      :rules="[rules.required, rules.max200]"
      counter="200"
    />

    <div class="mb-1"><small class="grey--text">Text</small></div>
    <div class="mb-1">
      <small class="text--disabled text-caption"
        >Doporučeno max. 150 znaků, jinak se na mobilu může zkracovat</small
      >
    </div>
    <v-textarea
      v-model="form.body"
      label="Text notifikace"
      solo
      outlined
      flat
      :rules="[rules.max200]"
      counter="200"
      rows="4"
      class="mb-2"
    />

    <div class="mb-1"><small class="grey--text">Odkaz (volitelné)</small></div>
    <v-row class="mb-0" no-gutters align="center">
      <v-col cols="auto" class="mr-4">
        <v-switch
          v-model="isExternalLink"
          :label="isExternalLink ? 'Externí odkaz' : 'Interní odkaz'"
          hide-details
          class="mt-0 pt-0"
          @change="form.actionLink = ''"
        />
      </v-col>
    </v-row>

    <div v-if="!isExternalLink" class="mt-3">
      <v-tabs
        v-model="activeProductTab"
        dense
        background-color="transparent"
        class="mb-1"
      >
        <v-tab>Rybelsus</v-tab>
        <v-tab>Wegovy</v-tab>
        <v-tab>Norditropin</v-tab>
        <v-tab>Sdílené</v-tab>
      </v-tabs>
      <v-combobox
        v-model="internalActionLink"
        :items="currentTabRoutes"
        item-text="value"
        item-value="value"
        :filter="routeFilter"
        label="Cesta v aplikaci"
        solo
        outlined
        flat
        clearable
        no-data-text="Žádná shoda – lze zadat vlastní cestu"
      >
        <template #item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
            <v-list-item-subtitle class="text--disabled text-caption">{{
              item.value
            }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-combobox>
    </div>

    <InputElement
      v-else
      v-model="form.actionLink"
      label="URL odkazu (https://...)"
      :rules="form.actionLink ? [rules.httpsUrl, rules.max2000] : []"
      counter="2000"
      class="mt-3"
    />

    <div class="mb-2"><small class="grey--text">Příjemci *</small></div>
    <v-btn-toggle
      v-model="recipientsMode"
      mandatory
      class="mb-4"
      color="primary"
    >
      <v-btn value="excel" small outlined>
        <v-icon small left>mdi-file-excel-outline</v-icon>
        Excel soubor
      </v-btn>
      <v-btn value="customQuery" small outlined>
        <v-icon small left>mdi-account-group-outline</v-icon>
        Skupina
      </v-btn>
      <v-btn value="explicitIds" small outlined>
        <v-icon small left>mdi-account-search-outline</v-icon>
        Výběr pacientů
      </v-btn>
    </v-btn-toggle>

    <div v-if="recipientsMode === 'excel'">
      <div v-if="editId" class="text-caption grey--text mb-1">
        Pro uložení změn je nutné nahrát Excel soubor znovu.
      </div>
      <v-file-input
        v-model="xlsxFile"
        label="Nahrát Excel soubor (.xlsx)"
        accept=".xlsx"
        solo
        outlined
        flat
        prepend-icon=""
        prepend-inner-icon="mdi-file-excel-outline"
        :rules="[rules.requiredFile]"
        class="mb-2"
      />
      <a
        href="/example-recipients.xlsx"
        download
        class="text-caption primary--text"
        >Vzorový soubor ke stažení</a
      >
    </div>

    <div v-if="recipientsMode === 'customQuery'">
      <InputSelectElement
        v-model="form.customQueryId"
        label="Skupina příjemců"
        :items="customQueryOptions"
        :loading="customQueriesLoading"
        :rules="recipientsMode === 'customQuery' ? [rules.requiredFile] : []"
      />
    </div>

    <div v-if="recipientsMode === 'explicitIds'">
      <v-autocomplete
        v-model="selectedPatients"
        :items="patientSearchItems"
        :loading="patientSearchLoading"
        :search-input.sync="patientSearch"
        :item-text="patientItemText"
        item-value="id"
        return-object
        no-filter
        multiple
        chips
        small-chips
        deletable-chips
        label="Vyhledat pacienta (email nebo ID)"
        solo
        outlined
        flat
        prepend-inner-icon="mdi-account-search-outline"
        :rules="
          recipientsMode === 'explicitIds' ? [rules.requiredPatients] : []
        "
      >
        <template #no-data>
          <v-list-item>
            <v-list-item-content>
              <v-list-item-title class="grey--text text--darken-1 text-body-2">
                {{
                  patientSearch && patientSearch.length >= 1
                    ? "Nebyly nalezeny žádné výsledky"
                    : "Začněte psát pro vyhledání pacienta"
                }}
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </template>
      </v-autocomplete>
    </div>

    <div class="d-flex justify-end mt-4">
      <ButtonElement
        secondary
        text="Zrušit"
        class="mr-2"
        @click="$emit('close')"
      />
      <ButtonElement
        :text="editId ? 'Uložit změny' : 'Připravit notifikaci'"
        :loading="loading"
        @click="handleSubmit"
      />
    </div>
  </v-form>
</template>

<script>
import useNotifications from "../../use/useNotifications";
import InputElement from "../elements/InputElement.vue";
import InputSelectElement from "../elements/InputSelectElement.vue";
import ButtonElement from "../elements/ButtonElement.vue";

export default {
  name: "NotificationForm",

  components: {
    InputElement,
    InputSelectElement,
    ButtonElement,
  },

  props: {
    editId: { type: Number, default: null },
    initialData: { type: Object, default: null },
  },

  setup() {
    const {
      prepareNotification,
      updatePreparedNotification,
      getCustomQueries,
      getCompactUsers,
      getPreparedNotificationRecipients,
    } = useNotifications();
    return {
      prepareNotification,
      updatePreparedNotification,
      getCustomQueries,
      getCompactUsers,
      getPreparedNotificationRecipients,
    };
  },

  data() {
    return {
      loading: false,
      customQueriesLoading: false,
      isExternalLink: false,
      activeProductTab: 0,
      recipientsMode: "excel",
      xlsxFile: null,
      customQueries: [],
      selectedPatients: [],
      patientSearchItems: [],
      patientSearchLoading: false,
      patientSearch: "",
      appRoutes: {
        rybelsus: [
          { text: "Deník", value: "/app/diary" },
          { text: "Kalendář", value: "/app/calendar" },
          {
            text: "Průvodce léčbou - rozcestník",
            value: "/app/rybelsus/guide",
          },
          {
            text: "Průvodce léčbou - rozcestník (alternativní vstup)",
            value: "/app/guide",
          },
          { text: "Návod k použití", value: "/app/guide/usage" },
          { text: "Schéma dávkování", value: "/app/guide/dosage" },
          { text: "Skladování", value: "/app/guide/storage" },
          { text: "Nežádoucí účinky", value: "/app/guide/side-effects" },
          {
            text: "Prevence nežádoucích účinků",
            value: "/app/guide/avoiding-side-effects",
          },
          {
            text: "Pomoc při nežádoucích účincích",
            value: "/app/guide/side-effect-assistance",
          },
          { text: "Časté otázky", value: "/app/guide/faq" },
          { text: "Pojištění / úhrada", value: "/app/insurance" },
          { text: "Glykovaný hemoglobin", value: "/app/hemoglobin" },
          { text: "Kroky", value: "/app/steps" },
        ],
        wegovy: [
          { text: "Deník", value: "/app/saxenda/diary" },
          { text: "Kalendář", value: "/app/saxenda/calendar" },
          { text: "Průvodce léčbou - rozcestník", value: "/app/saxenda/guide" },
          { text: "Návod k použití", value: "/app/saxenda/guide/usage" },
          { text: "Schéma dávkování", value: "/app/saxenda/guide/dosage" },
          { text: "Skladování", value: "/app/saxenda/guide/storage" },
          {
            text: "Nežádoucí účinky",
            value: "/app/saxenda/guide/side-effects",
          },
          {
            text: "Prevence nežádoucích účinků",
            value: "/app/saxenda/guide/avoiding-side-effects",
          },
          {
            text: "Pomoc při nežádoucích účincích",
            value: "/app/saxenda/guide/side-effect-assistance",
          },
          { text: "Časté otázky", value: "/app/saxenda/guide/faq" },
          {
            text: "Zdravý životní styl - přehled videí",
            value: "/app/saxenda/healthy-life-style",
          },
          {
            text: "Zdravý životní styl - detail videa",
            value: "/app/saxenda/healthy-life-style/:id",
          },
          {
            text: "Nutriční terapeut - přístupový kód",
            value: "/app/saxenda/nutrition-consultation",
          },
          { text: "Kroky", value: "/app/steps" },
          { text: "Výzvy - rozcestník", value: "/app/challenges" },
          { text: "Výzva - strava", value: "/app/challenges/food" },
          {
            text: "Výzva - ovoce a zelenina",
            value: "/app/challenges/food/vegetables",
          },
          {
            text: "Výzva - příjem bílkovin",
            value: "/app/challenges/food/proteins",
          },
          { text: "Výzva - konzumace ryb", value: "/app/challenges/food/fish" },
          {
            text: "Výzva - emoční stravování",
            value: "/app/challenges/food-emotions",
          },
          { text: "Výzva - mentální zdraví", value: "/app/challenges/mental" },
          { text: "Výzva - cíle", value: "/app/challenges/mental/goals" },
          {
            text: "Výzva - motivace",
            value: "/app/challenges/mental/motivation",
          },
          {
            text: "Výzva - radosti a starosti",
            value: "/app/challenges/mental/joys-worries",
          },
          {
            text: "Výzva - váha a obvod pasu",
            value: "/app/challenges/weight-waist",
          },
          { text: "Výzva - pohyb", value: "/app/challenges/movement" },
          {
            text: "Výzva - pohyb - historie",
            value: "/app/challenges/movement/history",
          },
          {
            text: "Výzva - životní styl",
            value: "/app/challenges/lifestyle/:type",
          },
          {
            text: "Výzva - pohyb - týden",
            value: "/app/challenges/movement/week/:week",
          },
          {
            text: "Výzva - pohyb - souhrn týdne",
            value: "/app/challenges/movement/week/:week/summary",
          },
        ],
        norditropin: [
          { text: "Deník", value: "/app/norditropin/diary" },
          { text: "Kalendář", value: "/app/norditropin/calendar" },
          {
            text: "Průvodce léčbou - rozcestník",
            value: "/app/norditropin/guide",
          },
          {
            text: "Příprava pera",
            value: "/app/norditropin/guide/pen-preparation",
          },
          {
            text: "Aplikace dávky",
            value: "/app/norditropin/guide/dose-application",
          },
          {
            text: "Čemu se vyhnout",
            value: "/app/norditropin/guide/what-to-avoid",
          },
          { text: "Růstové křivky", value: "/app/norditropin/growth-curves" },
        ],
        shared: [
          { text: "Domovská obrazovka", value: "/app/home" },
          {
            text: "První přihlášení / dokončení registrace",
            value: "/app/first-login",
          },
          { text: "Profil uživatele", value: "/app/profile" },
          { text: "Podmínky použití", value: "/app/terms" },
          { text: "Souhlas", value: "/app/consent" },
          { text: "Změna hesla", value: "/app/reset-password" },
          { text: "Hmotnost", value: "/app/weight" },
          { text: "Žádosti o péči", value: "/app/care-requests" },
        ],
      },
      form: {
        name: "",
        title: "",
        body: "",
        actionLink: "",
        customQueryId: null,
      },
      rules: {
        required: (v) => !!v || "Toto pole je povinné",
        requiredFile: (v) =>
          !!v || "Je povinné nahrát soubor nebo vybrat skupinu uživatelů",
        requiredPatients: (v) =>
          !!(v && v.length) || "Je povinné vybrat alespoň jednoho příjemce",
        max200: (v) => !v || v.length <= 200 || "Maximálně 200 znaků",
        max2000: (v) => !v || v.length <= 2000 || "Maximálně 2000 znaků",
        httpsUrl: (v) =>
          !v || /^https?:\/\/.+/.test(v) || "Odkaz musí začínat https://",
      },
    };
  },

  computed: {
    customQueryOptions() {
      return this.customQueries.map((q) => ({ text: q.name, value: q.id }));
    },

    currentTabRoutes() {
      const keys = ["rybelsus", "wegovy", "norditropin", "shared"];
      return this.appRoutes[keys[this.activeProductTab]] || [];
    },

    internalActionLink: {
      get() {
        return this.form.actionLink;
      },
      set(v) {
        this.form.actionLink = v && typeof v === "object" ? v.value : v || "";
      },
    },
  },

  watch: {
    patientSearch(val) {
      if (!val || val.length < 1) return;
      clearTimeout(this.patientSearchTimer);
      this.patientSearchTimer = setTimeout(() => this.searchPatients(val), 300);
    },
  },

  created() {
    this.patientSearchTimer = null;
  },

  async mounted() {
    await this.fetchCustomQueries();
    if (this.initialData) {
      await this.initFromData(this.initialData);
    }
  },

  methods: {
    async initFromData(data) {
      this.form.name = data.name || "";
      this.form.title = data.title || "";
      this.form.body = data.body || "";
      this.form.actionLink = data.actionLink || "";
      this.isExternalLink = !!(
        data.actionLink && /^https?:\/\//.test(data.actionLink)
      );
      const sourceType = (data.recipientSourceType || "").toLowerCase();
      if (sourceType === "customquery") {
        this.recipientsMode = "customQuery";
        this.form.customQueryId = data.customQuery?.id || null;
      } else if (sourceType === "explicitids") {
        this.recipientsMode = "explicitIds";
        await this.loadExplicitRecipients();
      } else {
        this.recipientsMode = "excel";
      }
    },

    async loadExplicitRecipients() {
      if (!this.editId) return;
      try {
        const PAGE_SIZE = 100;
        let skip = 0;
        let allRecipients = [];
        let hasMore = true;
        while (hasMore) {
          const result = await this.getPreparedNotificationRecipients(
            this.editId,
            { skip, take: PAGE_SIZE },
          );
          if (!result) break;
          const page = (result.recipients || []).map((r) => ({
            id: r.id,
            email: r.email,
            fullName: r.fullName || null,
          }));
          allRecipients = allRecipients.concat(page);
          hasMore = page.length === PAGE_SIZE;
          skip += PAGE_SIZE;
        }
        this.selectedPatients = allRecipients;
        this.patientSearchItems = allRecipients;
      } catch {
        // leave selectedPatients empty if load fails
      }
    },

    async fetchCustomQueries() {
      this.customQueriesLoading = true;
      try {
        const result = await this.getCustomQueries();
        if (result) {
          this.customQueries = result.customQueries || [];
        }
      } finally {
        this.customQueriesLoading = false;
      }
    },

    async searchPatients(query) {
      this.patientSearchLoading = true;
      try {
        const result = await this.getCompactUsers({ searchQuery: query });
        if (result) {
          const selectedIds = new Set(this.selectedPatients.map((p) => p.id));
          const newItems = (result.users || []).filter(
            (u) => !selectedIds.has(u.id),
          );
          this.patientSearchItems = [...this.selectedPatients, ...newItems];
        }
      } finally {
        this.patientSearchLoading = false;
      }
    },

    routeFilter(item, queryText) {
      const q = queryText.toLowerCase();
      return (
        item.text.toLowerCase().includes(q) ||
        item.value.toLowerCase().includes(q)
      );
    },

    patientItemText(item) {
      return item.fullName ? `${item.fullName} (${item.email})` : item.email;
    },

    handleSubmit() {
      if (!this.$refs.form.validate()) {
        this.scrollToFirstError();
        return;
      }
      this.submitForm();
    },

    scrollToFirstError() {
      this.$nextTick(() => {
        const errorEl = this.$el.querySelector(".v-messages.error--text");
        if (errorEl) {
          errorEl.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      });
    },

    async submitForm() {
      this.loading = true;
      try {
        const isExplicit = this.recipientsMode === "explicitIds";
        const isCustom = this.recipientsMode === "customQuery";
        const notification = {
          name: this.form.name,
          title: this.form.title,
          body: this.form.body || null,
          type: "AdminNotification",
          actionLink: this.form.actionLink || null,
          recipientSourceType: isCustom
            ? "customQuery"
            : isExplicit
            ? "explicitIds"
            : "excelUpload",
          recipientsIds: isExplicit
            ? this.selectedPatients.map((p) => p.id)
            : null,
          customQueryId: isCustom ? this.form.customQueryId : null,
        };

        const recipientsExcel =
          this.recipientsMode === "excel" ? this.xlsxFile : null;
        const response = this.editId
          ? await this.updatePreparedNotification(
              this.editId,
              notification,
              recipientsExcel,
            )
          : await this.prepareNotification(notification, recipientsExcel);
        if (response) {
          this.$emit(this.editId ? "updated" : "submitted");
          this.resetForm();
        }
      } finally {
        this.loading = false;
      }
    },

    resetForm() {
      this.form = {
        name: "",
        title: "",
        body: "",
        actionLink: "",
        customQueryId: null,
      };
      this.recipientsMode = "excel";
      this.xlsxFile = null;
      this.isExternalLink = false;
      this.selectedPatients = [];
      this.patientSearchItems = [];
      this.patientSearch = "";
      this.$refs.form.resetValidation();
    },
  },
};
</script>
